import React, { useState } from 'react';
import VolunteerItem from './VolunteerItem';

const VolunteerList = ({ volunteers, onEdit, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');

  // Get unique districts for filter dropdown
  const districts = [...new Set(volunteers.map(volunteer => volunteer.district).filter(Boolean))];

  // Filter volunteers based on search term and district filter
  const filteredVolunteers = volunteers.filter(volunteer => {
    const matchesSearch =
      (volunteer.volunteerName && volunteer.volunteerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (volunteer.mobile && volunteer.mobile.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (volunteer.village && volunteer.village.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (volunteer.username && volunteer.username.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesDistrict = districtFilter === '' || volunteer.district === districtFilter;

    return matchesSearch && matchesDistrict;
  });

  return (
    <div style={styles.container}>
      <div style={styles.filterSection}>
        <div style={styles.filterHeader}>
          <div style={styles.filterTitle}>
            <span style={styles.filterIcon}>🔍</span>
            <span>Search & Filter</span>
          </div>
        </div>

        <div style={styles.filters}>
          <div style={styles.searchBox}>
            <input
              type="text"
              placeholder="Search volunteers by name, mobile or village"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={styles.clearButton}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <div style={styles.filterBox}>

            <select
              value={districtFilter}
              onChange={(e) => setDistrictFilter(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="">All Districts</option>
              {districts.map((district, index) => (
                <option key={index} value={district}>{district}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.listHeader}>
          <div style={styles.headerName}>
            <span style={styles.headerIcon}>👨‍🌾</span>
            Volunteer Name
          </div>
          <div style={styles.headerMobile}>
            <span style={styles.headerIcon}>📱</span>
            Mobile
          </div>
          <div style={styles.headerVillage}>
            <span style={styles.headerIcon}>🏡</span>
            Village
          </div>
          <div style={styles.headerDistrict}>
            <span style={styles.headerIcon}>🗺️</span>
            District
          </div>
          <div style={styles.headerStatus}>
            <span style={styles.headerIcon}>🔄</span>
            Status
          </div>
          <div style={styles.headerActions}>Actions</div>
        </div>

        <div style={styles.list}>
          {filteredVolunteers.length > 0 ? (
            filteredVolunteers.map(volunteer => (
              <VolunteerItem
                key={volunteer.id}
                volunteer={volunteer}
                onEdit={() => onEdit(volunteer)}
                onDelete={() => onDelete(volunteer.id)}
              />
            ))
          ) : (
            <div style={styles.noResults}>
              <div style={styles.noResultsIcon}>🔍</div>
              <div style={styles.noResultsText}>No volunteers found matching your criteria.</div>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setDistrictFilter('');
                }}
                style={styles.resetButton}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '0',
  },
  filterSection: {
    backgroundColor: 'white',
    borderRadius: '8px 8px 0 0',
    padding: '1.5rem',
    borderBottom: '1px solid var(--border-color)',
  },
  filterHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  filterTitle: {
    fontSize: '1.1rem',
    fontWeight: 'bold',
    color: 'var(--text-color)',
    display: 'flex',
    alignItems: 'center',
  },
  filterIcon: {
    marginRight: '0.5rem',
  },
  resultsCount: {
    fontSize: '0.9rem',
    color: 'var(--text-light)',
    backgroundColor: 'var(--background-color)',
    padding: '0.4rem 0.8rem',
    borderRadius: '20px',
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  searchBox: {
    flex: '1 1 300px',
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'var(--text-light)',
    fontSize: '0.9rem',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 2.5rem',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'var(--background-color)',
    transition: 'all 0.3s',
  },
  clearButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: 'var(--text-light)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  filterBox: {
    flex: '0 0 200px',
    display: 'flex',
    flexDirection: 'column',
  },
  filterLabel: {
    fontSize: '1rem',
    marginBottom: '0.2rem',
    color: 'var(--text-light)',
  },
  filterSelect: {
    width: '100%',
    padding: '0.5rem',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    fontSize: '1rem',
    backgroundColor: 'var(--background-color)',
    height: '46px',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '0 0 8px 8px',
  },
  listHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: 'var(--primary-light)',
    color: 'white',
    fontWeight: 'bold',
    borderBottom: '2px solid var(--primary-color)',
  },
  headerIcon: {
    marginRight: '0.5rem',
    fontSize: '0.9rem',
  },
  headerName: {
    display: 'flex',
    alignItems: 'center',
  },
  headerMobile: {
    display: 'flex',
    alignItems: 'center',
  },
  headerVillage: {
    display: 'flex',
    alignItems: 'center',
  },
  headerDistrict: {
    display: 'flex',
    alignItems: 'center',
  },
  headerStatus: {
    display: 'flex',
    alignItems: 'center',
  },
  headerActions: {
    textAlign: 'center',
  },
  list: {
    padding: '0.5rem',
  },
  noResults: {
    padding: '3rem 1rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  noResultsIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    opacity: '0.5',
  },
  noResultsText: {
    marginBottom: '1rem',
  },
  resetButton: {
    padding: '0.5rem 1rem',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
  },
};

export default VolunteerList;
